import { forwardRef, type ReactNode } from "react";
import { titleStatus } from "@/components/format";

export function Splash({ message }: { message: string }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-zinc-50 text-zinc-500">
      <p className="text-sm">{message}</p>
    </div>
  );
}

export function WorkspaceHeader({ title, detail, action }: { title: string; detail: string; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-1 text-sm text-zinc-500">{detail}</p>
      </div>
      {action}
    </div>
  );
}

// Button looks for the page body: secondary is a light brand tint, primary the strong brand green.
// Disabled is neutral grey in every tone, so it reads as unavailable rather than as a paler colour.
const disabledButtonClass = "disabled:border-zinc-200 disabled:bg-zinc-100 disabled:text-zinc-400";
export const secondaryButtonClass = `border-brand/40 bg-brand-light/15 text-brand-ink hover:border-brand hover:bg-brand-light/30 ${disabledButtonClass}`;
export const primaryButtonClass = `border-brand-strong bg-brand-strong text-white hover:bg-brand-stronger ${disabledButtonClass}`;
export const dangerButtonClass = `border-red-200 bg-red-50 text-red-700 hover:bg-red-100 ${disabledButtonClass}`;

export const ToolbarButton = forwardRef<
  HTMLButtonElement,
  {
    children: ReactNode;
    disabled?: boolean;
    onClick: () => void;
    tone?: "default" | "primary" | "danger";
    "aria-label"?: string;
    className?: string;
  }
>(function ToolbarButton({ children, disabled, onClick, tone = "default", "aria-label": ariaLabel, className = "" }, ref) {
  const cls = tone === "danger" ? dangerButtonClass : tone === "primary" ? primaryButtonClass : secondaryButtonClass;
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      className={`rounded-md border px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed ${cls} ${className}`.trim()}
    >
      {children}
    </button>
  );
});

export function StatusBadge({ status }: { status: string }) {
  const cls = {
    pending_approval: "bg-amber-50 text-amber-700 ring-amber-200",
    approved: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    generating: "bg-sky-50 text-sky-700 ring-sky-200",
    generated: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    failed: "bg-red-50 text-red-700 ring-red-200",
    rejected: "bg-zinc-100 text-zinc-700 ring-zinc-200",
    revoked: "bg-red-50 text-red-700 ring-red-200",
    valid: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    not_found: "bg-zinc-100 text-zinc-700 ring-zinc-200",
    pending: "bg-amber-50 text-amber-700 ring-amber-200",
    unconfirmed: "bg-amber-50 text-amber-700 ring-amber-200",
    confirmed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    draft: "bg-amber-50 text-amber-700 ring-amber-200",
    published: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    queued: "bg-sky-50 text-sky-700 ring-sky-200",
    sending: "bg-sky-50 text-sky-700 ring-sky-200",
    ready: "bg-amber-50 text-amber-700 ring-amber-200",
    sent: "bg-sky-50 text-sky-700 ring-sky-200",
    distributing: "bg-sky-50 text-sky-700 ring-sky-200",
    completed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    delivered: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    printed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    cancelled: "bg-slate-100 text-slate-600 ring-slate-300",
  }[status] ?? "bg-zinc-100 text-zinc-700 ring-zinc-200";
  return <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ring-1 ${cls}`}>{titleStatus(status)}</span>;
}

export function StatusDot({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs font-medium text-zinc-500">
      <span className="h-2 w-2 rounded-full bg-emerald-500" />
      {label}
    </span>
  );
}

export function Metric({ label, value, tone = "default" }: { label: string; value: number; tone?: "default" | "danger" }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className={tone === "danger" ? "text-lg font-semibold text-red-700" : "text-lg font-semibold text-zinc-950"}>{value}</div>
    </div>
  );
}

export function EmptyState({ title, detail, compact = false }: { title: string; detail: string; compact?: boolean }) {
  return (
    <div className={compact ? "rounded-md bg-zinc-50 p-4" : "p-8"}>
      <p className="text-sm font-medium text-zinc-800">{title}</p>
      <p className="mt-1 text-sm text-zinc-500">{detail}</p>
    </div>
  );
}

export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}

export function DetailField({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-zinc-500">{label}</dt>
      <dd className={mono ? "break-all font-mono text-xs text-zinc-800" : "text-zinc-800"}>{value}</dd>
    </div>
  );
}
